/**
 * RenovationsSelectionPopup — native wall/floor renovation picker (lifted from
 * `common/StagingFlow/RoomItems/RenovationsSelectionPopup.component.tsx`, rewired
 * onto the module working context + `patchRoom` + plain-JS `renovations` pair
 * array). Options from the static `VirtualFurnitureUpgrades` catalogue
 * (`components/contexts/*` — ALLOWED; no session-keyed price). Analytics
 * dropped (DF-staging-analytics).
 */

import { FC, useCallback, useMemo } from 'react'

import { StagingItemSelectionPopup, StagingItemSelectionTab } from 'components/common/StagingFlow/StagingItemSelectionPopup'
import { VirtualFurnitureRoomType } from 'constants/virtualFurniture'
import { useTranslation } from 'react-i18next'
import { useVirtualFurnitureUpgrades } from 'components/contexts/VirtualFurnitureUpgradesContext'

import { RoomItemPopupType, useStagingWorkingState } from '../StagingWorkingState.context'
import { mapDelete, mapGet, mapSet } from '../stagingRoom.logic'

export const RenovationsSelectionPopup: FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const { t } = useTranslation(['style_selection_order_step'])
  const { virtualFurnitureUpgradesMap } = useVirtualFurnitureUpgrades()
  const { activeRoom, roomItemPopupType, setRoomItemPopupType, patchRoom } = useStagingWorkingState()

  const availableRenovations = useMemo(() => {
    switch (roomItemPopupType) {
      case RoomItemPopupType.FLOOR:
        return virtualFurnitureUpgradesMap.get(VirtualFurnitureRoomType.FLOORING) ?? []
      case RoomItemPopupType.WALL:
        return virtualFurnitureUpgradesMap.get(VirtualFurnitureRoomType.WALL_MATERIALS) ?? []
      default:
        return []
    }
  }, [roomItemPopupType, virtualFurnitureUpgradesMap])

  const popupTitle = useMemo(() => {
    switch (roomItemPopupType) {
      case RoomItemPopupType.FLOOR:
        return t('style_selection_order_step:room_items.FLOOR')
      case RoomItemPopupType.WALL:
        return t('style_selection_order_step:room_items.WALL')
      default:
        return ''
    }
  }, [roomItemPopupType, t])

  const selectedRenovation = useMemo(() => {
    if (!activeRoom) return undefined
    switch (roomItemPopupType) {
      case RoomItemPopupType.FLOOR:
        return mapGet(activeRoom.renovations, VirtualFurnitureRoomType.FLOORING)
      case RoomItemPopupType.WALL:
        return mapGet(activeRoom.renovations, VirtualFurnitureRoomType.WALL_MATERIALS)
      default:
        return undefined
    }
  }, [activeRoom, roomItemPopupType])

  const popupOnSelect = useCallback(
    (selectedStyle: string | undefined) => {
      if (!activeRoom) return
      switch (roomItemPopupType) {
        case RoomItemPopupType.FLOOR:
          patchRoom(activeRoom.key, {
            renovations: selectedStyle
              ? mapSet(activeRoom.renovations, VirtualFurnitureRoomType.FLOORING, selectedStyle)
              : mapDelete(activeRoom.renovations, VirtualFurnitureRoomType.FLOORING),
          })
          break
        case RoomItemPopupType.WALL:
          patchRoom(activeRoom.key, {
            renovations: selectedStyle
              ? mapSet(activeRoom.renovations, VirtualFurnitureRoomType.WALL_MATERIALS, selectedStyle)
              : mapDelete(activeRoom.renovations, VirtualFurnitureRoomType.WALL_MATERIALS),
          })
          break
        default:
          break
      }
      setRoomItemPopupType(null)
    },
    [activeRoom, roomItemPopupType, setRoomItemPopupType, patchRoom]
  )

  if (!activeRoom) return null

  return (
    <StagingItemSelectionPopup
      availableItems={{ [StagingItemSelectionTab.ALL]: availableRenovations }}
      title={popupTitle}
      isOpen={isOpen}
      selectedItems={{ [StagingItemSelectionTab.ALL]: selectedRenovation || '' }}
      onClose={() => setRoomItemPopupType(null)}
      onSelect={(selected) => popupOnSelect(selected[StagingItemSelectionTab.ALL])}
    />
  )
}
